import {datadogReporter} from '../reporters/datadog';
import {browserPerformanceApiStorage} from '../storages/browserPerformanceApi';
import {PerformanceLatencyEngine} from '../types';
import {addStep, finishAndReport, start} from './core';

/**
 * Implements a series of tools to measure and report latency. This reporter
 * is geared specifically towards the browser environment (using Performance API)
 * and using Datadog as the reporting engine.
 *
 * If you want to build the same tools with a different performance API or
 * a different reporting engine, you can use the core utils in `./core` to
 * build your own.
 *
 * To measure a latency from a given event, you can use the following steps:
 *
 * 1. Start the measurement with `start(key)`. You can optionally pass extra data as the second
 *    argument and measurement options as the third argument.
 * 2. (optional) Add steps to the measurement with `addStep(key, stepName)`. Steps
 *    are additional readings you want to measure and add to the report. They are
 *    handy if you to measure a given event but also measure substeps.
 * 3. Finish the measurement and report it with `finishAndReport(key)`.
 *
 * You can pass extra data you want to send to the reporter as the second argument
 * for both `start` and `finishAndReport`.
 *
 * If `start` is not called before `finishAndReport`, the measurement will not be reported.
 */
const ClientLatencyTools: PerformanceLatencyEngine = {
  /**
   * Starts a latency measurement.
   *
   * Usage
   * ```
   * ClientLatencyTools.start('my-latency-key');
   * ```
   *
   * If you want to pass additional data to the reporter, you can pass it as the second argument.
   *
   * ```
   * ClientLatencyTools.start('my-latency-key', {feature: 'my-feature'});
   * ```
   *
   * You can also pass options as the third argument to customize the measurement behavior.
   * Currently supported options:
   *
   * - `measureFromOrigin`: When set to true, the measurement will start from the time origin (0)
   *   instead of the current time. This is useful for measuring latency from application start.
   *
   * ```
   * ClientLatencyTools.start('my-latency-key', {}, {measureFromOrigin: true});
   * ```
   *
   * With both extra data and options:
   *
   * ```
   * ClientLatencyTools.start('my-latency-key', {feature: 'my-feature'}, {measureFromOrigin: true});
   * ```
   */
  start: start(browserPerformanceApiStorage),

  /**
   * Cancels a latency measurement. This will clear all points for the given key.
   *
   * Usage
   * ```
   * ClientLatencyTools.cancel('my-latency-key');
   * ```
   */
  cancel: (key: string) => {
    browserPerformanceApiStorage.clearPoints(key);
  },

  /**
   * Adds a step to a latency measurement. A step is an additional point in time that you want to measure
   * and add to the report. If a start measurement has not been initiated, this will not do anything.
   *
   * Usage
   * ```
   * ClientLatencyTools.addStep('my-latency-key', 'my-step');
   * ```
   */
  addStep: addStep(browserPerformanceApiStorage),

  /**
   * Finishes a latency measurement and reports it. If a start measurement has not been initiated,
   * this will not do anything.
   *
   * Usage
   * ```
   * ClientLatencyTools.finishAndReport('my-latency-key');
   * ```
   *
   * If you want to pass additional data to the reporter, you can pass it as the second argument.
   *
   * ```
   * ClientLatencyTools.finishAndReport('my-latency-key', {result: 'success'});
   * ```
   *
   * You can also conditionally report based on the merged data by passing a `shouldReport` callback
   * as the third argument. The callback receives the merged data (from both `start` and `finishAndReport`)
   * and should return `true` to report or `false` to skip reporting.
   *
   * ```
   * ClientLatencyTools.finishAndReport(
   *   'my-latency-key',
   *   {endValue: 'foo'},
   *   {
   *     shouldReport: (data) => data.startValue === data.endValue
   *   }
   * );
   * ```
   */
  finishAndReport: finishAndReport(browserPerformanceApiStorage)(
    datadogReporter
  ),
};

export default ClientLatencyTools;
